import { useEffect, useRef, useState } from "react";
import { colors, components, typography } from "../../../design/tokens";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";
import { useProjectActions } from "../../../core_stores/useProjectStore";
import {
  HttpAssistantIsWorkingException,
  HttpNotEnoughTokensException,
} from "../../../model/httpException";
import { usePromptLibraryModal } from "../../../providers/PromptLibraryModalProvider";
import { useSnackbar } from "../../../providers/SnackbarProvider";
import { formatNumberWithSpaces } from "../../../utils/numberFormatter";
import { useChatMessagesActions } from "../../../view_stores/chat_messages/useChatMessagesStore";
import { useIsProjectProcessing } from "../../../view_stores/useProjectStatusStore";
import {
  ALLOWED_IMAGE_TYPES,
  useAttachedImage,
  useMaxPromptLength,
  usePrompt,
  usePromptInputActions,
} from "../../../view_stores/usePromptInputStore";
import {
  useCanHandleTechnicalError,
  useTechnicalErrorHandlerActions,
} from "../../../view_stores/useTechnicalErrorHandlerStore";
import { AttachedImageIcon } from "../../common_components/AttachedImageIcon";
import { AttachmentsBtn } from "../../common_components/AttachmentsBtn";

export function ProjectChatTextField() {
  const maxPromptLength = useMaxPromptLength();
  const prompt = usePrompt();
  const attachedImage = useAttachedImage();
  const canHandleTechnicalError = useCanHandleTechnicalError();

  const {
    setPrompt,
    setAttachedImage,
    isLengthExceeded: isPromptLengthExceeded,
    loadFromLocalStorage: loadPromptFromLocalStorage,
    clear: clearPromptInput,
  } = usePromptInputActions();
  const { addTemporaryMessage, removeTemporaryMessages } =
    useChatMessagesActions();
  const { sendMessage } = useProjectActions();
  const { rollback } = useTechnicalErrorHandlerActions();
  const { showSnackbar } = useSnackbar();
  const { open: openPromptLibrary } = usePromptLibraryModal();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPromptSubmitting, setIsPromptSubmitting] = useState(false);

  const isProcessing = useIsProjectProcessing();
  const isCompactView = useIsCompactView();

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height
    const newHeight = textarea.scrollHeight;

    // Set max height to ~40% of viewport
    const maxHeight = window.innerHeight * 0.4;

    // Apply height with constraints
    textarea.style.height = `${Math.min(newHeight, maxHeight)}px`;
  };

  useEffect(() => {
    loadPromptFromLocalStorage();
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [prompt]);

  useEffect(() => {
    if (isProcessing) {
      setIsPromptSubmitting(false);
    }
    // NOT_ENOUGH_TOKENS case removed - prompt is never cleared when this error occurs
    // because sendMessage() throws before clearPromptInput() is called
  }, [isProcessing]);

  const canSubmit =
    !isProcessing && prompt?.trim() && !isPromptLengthExceeded();

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setAttachedImage(file);
    } else {
      showSnackbar(
        `Unsupported file type. Please select JPEG, PNG, GIF, or WebP images.`,
        { type: "error" }
      );
    }
  };

  const handleRemoveImage = () => {
    setAttachedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handlePaste = async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;

    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file && ALLOWED_IMAGE_TYPES.includes(file.type)) {
          e.preventDefault();
          setAttachedImage(file);
          break;
        }
      }
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // On mobile: allow newline, don't send
      if (isCompactView) {
        return; // Let default behavior happen (newline)
      }

      // On desktop: send message (existing behavior)
      e.preventDefault();
      submit();
    }
  };

  const submit = async () => {
    if (canSubmit) {
      const promptToSend = prompt!.trim();
      setIsPromptSubmitting(true);
      try {
        if (canHandleTechnicalError) {
          await rollback(promptToSend, attachedImage);
          clearPromptInput();

          // Reset textarea height after sending
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
          }
        } else {
          await sendMessage(promptToSend, attachedImage);

          await addTemporaryMessage(promptToSend, attachedImage);
          clearPromptInput();

          // Reset textarea height after sending
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
          }

          setIsPromptSubmitting(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }
      } catch (error) {
        if (error instanceof HttpAssistantIsWorkingException) {
          removeTemporaryMessages();
          setPrompt(promptToSend);
          setAttachedImage(attachedImage);
          setIsPromptSubmitting(false);
          showSnackbar(
            "Previous action is still in progress, try again later.",
            {
              type: "error",
              duration: 5000,
            }
          );
        } else if (error instanceof HttpNotEnoughTokensException) {
          // Message was not sent - prompt never cleared, no temp message added
          // Just reset submitting state - error message handled by store subscription
          setIsPromptSubmitting(false);
        }
      }
    }
  };

  return (
    <div className="h-auto">
      <div
        className="bg-dark-surface-secondary
      rounded-xl p-2 h-full
      border border-dark-border
      flex flex-col
      shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        <textarea
          ref={textareaRef}
          className={`w-full bg-transparent resize-none focus:outline-none
          ${colors.text.primary} ${typography.size.sm}
          placeholder:text-dark-placeholder
          py-2 px-3`}
          style={{
            minHeight: '44px',
            maxHeight: '40vh',
            overflow: 'auto',
          }}
          value={prompt ?? ""}
          placeholder="What would you like to change?"
          onChange={(e) => {
            setPrompt(e.target.value);
            adjustTextareaHeight();
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          enterKeyHint="send"
        />
        <div className="flex justify-between items-center mt-2 gap-4">
          <div className="flex items-center gap-4">
            <AttachmentsBtn
              className="bg-dark-surface-tertiary
              hover:bg-dark-hover
              text-dark-text-muted hover:text-dark-text rounded-full transition-colors duration-200"
              onAddImageClick={handleUploadClick}
              onPromptLibraryClick={openPromptLibrary}
              iconSize={20}
              dropdownPosition="top"
            />
            {attachedImage && (
              <AttachedImageIcon
                attachedImg={attachedImage}
                onRemove={handleRemoveImage}
              />
            )}
          </div>
          <SubmitBtn
            isSubmitting={isPromptSubmitting}
            isDisabled={
              isPromptLengthExceeded() ||
              isPromptSubmitting ||
              isProcessing ||
              !prompt
            }
            onClick={submit}
          />
        </div>
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleImageUpload}
          className="hidden"
          ref={fileInputRef}
        />
      </div>
      {isPromptLengthExceeded() && (
        <div className="text-red-500 text-sm px-2 mt-1">
          Message is too long — max {formatNumberWithSpaces(maxPromptLength)}{" "}
          characters allowed.
        </div>
      )}
    </div>
  );
}

function SubmitBtn({
  isSubmitting,
  isDisabled,
  onClick,
}: {
  isSubmitting: boolean;
  isDisabled: boolean;
  onClick: () => void;
}) {
  return (
    <div className="flex gap-2">
      <button
        id="project-chat-text-field-send"
        className={components.button.primaryCompact}
        onClick={onClick}
        disabled={isDisabled}
      >
        {isSubmitting && (
          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
        )}
        Send
      </button>
    </div>
  );
}
